import {
    Article,
    CalendarDrawerContentDeliveryInformation,
    CalendarState,
    DeliveryInformation,
    RubberBandItem,
    SpiralItem,
} from "burde";
import { CalendarSizeDimensions } from "svg/constants";
import { D3Selection } from "svg/draw/draw-calendar";
import { Selection } from "d3";
import { getComponentProductsBySize, getArticleById } from "calendar/service";
import { DefaultCulture } from "ui/constants";

const drawText = (
    svg: Selection<any, any, any, any>,
    text: string,
    fontSize: number,
    y: number,
    bold: boolean,
) => {
    svg.append("text")
        .attr("x", "50%")
        .attr("y", y)
        .attr("font-size", fontSize)
        .attr("font-family", "roboto")
        .attr("font-weight", bold ? "bold" : "normal")
        .attr("fill", "black")
        .attr("text-anchor", "middle")
        .text(text);
};

const drawInformationPage = (
    base: D3Selection,
    calendar: CalendarState,
    deliveryInformation: CalendarDrawerContentDeliveryInformation | null,
    calendarDimensions: CalendarSizeDimensions,
    calendarId: number | null,
    spiral: SpiralItem | null,
    rubberband: RubberBandItem | null,
    article: Article | null,
) => {
    const customOrderId =
        deliveryInformation !== null ? deliveryInformation.customOrderId : null;

    let container = base.append("div").attr("class", "svg-container");
    let small = 54 * calendarDimensions.adjustmentFactor,
        large = 74 * calendarDimensions.adjustmentFactor;
    let svg = container
        .append("svg")
        .attr("width", "100%")
        .attr("height", "100%")
        .attr("preserveAspectRatio", "xMinYMin meet")
        .attr(
            "viewBox",
            `0 0 ${calendarDimensions.svgWidth} ${calendarDimensions.svgHeight}`,
        )
        .classed("svg-content", true);

    const culture = calendar.culture != "" ? calendar.culture : DefaultCulture;

    const eanEndpoint = customOrderId
        ? `api/ean/getean?orderId=${calendarId}&culture=${culture}&customOrderId=${customOrderId}`
        : `api/ean/getean?orderId=${calendarId}&culture=${culture}`;

    fetch(eanEndpoint)
        .then((res) => res.text())
        .then((eanSvg) => {
            let encodedEanSvg = btoa(eanSvg);

            svg.append("svg:image")
                .attr("x", "0")
                .attr("y", "100")
                .attr(
                    "height",
                    400 * calendarDimensions.adjustmentFactor + "px",
                )
                .attr("width", "100%")
                .attr(
                    "xlink:href",
                    "data:image/svg+xml;base64," + encodedEanSvg,
                );
        });

    if (article !== null) {
        drawText(
            svg,
            article.sku + " : Kalender, " + calendar.calendarSize,
            large,
            1000 * calendarDimensions.adjustmentFactor,
            false,
        );
    }

    getComponentProductsBySize(calendar.calendarSize).then(
        (componentProducts) => {
            if (componentProducts.length > 0) {
                let componentProductYPos =
                    900 * calendarDimensions.adjustmentFactor;

                componentProducts.forEach((componentProduct) => {
                    drawText(
                        svg,
                        componentProduct.sku + " : " + componentProduct.text,
                        small,
                        componentProductYPos,
                        false,
                    );
                    if (
                        componentProductYPos ==
                        900 * calendarDimensions.adjustmentFactor
                    ) {
                        componentProductYPos +=
                            200 * calendarDimensions.adjustmentFactor;
                    } else {
                        componentProductYPos +=
                            100 * calendarDimensions.adjustmentFactor;
                    }
                });
            }
        },
    );

    let yPos = 1800 * calendarDimensions.adjustmentFactor;
    if (deliveryInformation) {
        const givenName = deliveryInformation.givenName
            ? deliveryInformation.givenName
            : "";
        const familyName = deliveryInformation.familyName
            ? deliveryInformation.familyName
            : "";

        drawText(svg, `${givenName} ${familyName}`, small, yPos, false);
        yPos += 100 * calendarDimensions.adjustmentFactor;
        drawText(
            svg,
            `${
                deliveryInformation.streetAddress !== null
                    ? deliveryInformation.streetAddress
                    : ""
            }`,
            small,
            yPos,
            false,
        );
        if (deliveryInformation.streetAddress2) {
            yPos += 100 * calendarDimensions.adjustmentFactor;
            drawText(
                svg,
                `${deliveryInformation.streetAddress2}`,
                small,
                yPos,
                false,
            );
        }
        yPos += 100 * calendarDimensions.adjustmentFactor;

        const postalCode = deliveryInformation.postalCode
            ? deliveryInformation.postalCode
            : "";
        const city = deliveryInformation.city ? deliveryInformation.city : "";

        drawText(svg, `${postalCode} ${city}`, small, yPos, false);
    }

    drawText(
        svg,
        spiral === null
            ? ""
            : spiral.articleNumber.toString() + " : " + spiral.text,
        large,
        700 * calendarDimensions.adjustmentFactor,
        true,
    );
    drawText(
        svg,
        rubberband == null
            ? ""
            : rubberband.articleNumber.toString() + " : " + rubberband.text,
        large,
        1500 * calendarDimensions.adjustmentFactor,
        true,
    );

    let blankPage = base.append("div").attr("class", "svg-container");
    blankPage
        .append("svg")
        .attr("width", "100%")
        .attr("height", "100%")
        .attr("preserveAspectRatio", "xMinYMin meet")
        .attr(
            "viewBox",
            `0 0 ${calendarDimensions.svgWidth} ${calendarDimensions.svgHeight}`,
        )
        .classed("svg-content", true);
};

export { drawInformationPage };
